import * as yup from "yup";
import { COLOR_DEFINITIONS } from "./getDefaultWhitelabelConfiguration";

// --- helpers ---
const semverRe =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-.]+)?(?:\+[0-9A-Za-z-.]+)?$/;
const hostnameRe =
  /^(?=.{1,253}$)(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))*$/;
const hexColorRe = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
const cssLengthRe = /^\d+(px|rem|em|vh|vw|%)$/i;

const urlStr = yup.string().url();

const hexColorStr = yup
  .string()
  .test("is-hex-or-empty", function (value) {
    if (!value) return true; // allow empty string, null, or undefined
    return hexColorRe.test(value) || this.createError({
      message: `${this.path} must be a hex color`,
    });
  });

const cssLengthStr = yup
  .string()
  .test("is-css-length", function (value) {
    if (!value) return true; // allow empty string, null, or undefined
    return cssLengthRe.test(value) || this.createError({
      message: `${this.path} must be a CSS length`,
    });
  })


// --- head item (localized) ---
const headItemSchema = yup.object({
  locale: yup.string().required(),
  title: yup.string().optional(),
  description: yup.string().optional(),
  meta: yup.object({
    robots: yup.string().optional(),
    og: yup.object({
      title: yup.string().optional(),
      description: yup.string().optional(),
      image: yup.string().optional(),
    }).optional(),
  }).optional(),
});

const linkItem = yup.object({
  locale: yup.string().required(),
  label: yup.string().required(),
  url: urlStr.required(),
});

const trackerItem = yup.object({
  enabled: yup.boolean().required(),
  id: yup.string().when('enabled', {
    is: true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  })
});


// --- communication.email schema ---
const communicationEmailSchema = yup.object({
  fromEmail: yup.string().email().required(),
  fromName: yup.string().required(),
  replyToEmail: yup.string().email().required(),
  replyToName: yup.string().required(),
  bccEmails: yup.array().of(yup.string().email().required()).default([]),
  localizedSubjects: yup.array().of(
    yup.object({
      subject: yup.string().required(),
      type: yup.string().required(), // tighten to enum if you have one
      locale: yup.string().required(),
    })
  ).default([]),
});

// --- main schema ---
export const whitelabelSchema = (allowedAffiliationCodes = []) =>
  yup.object({
    siteName: yup.string().required(),
    schemaVersion: yup.string().matches(semverRe, "Must be semver").required(),
    thirdPartyTrackers: yup.object({
      googleAnalytics: trackerItem.required(),
      cookieBot: trackerItem.required(),
      googleTagManager: trackerItem.required(),
    }).required(),
    domains: yup
      .array()
      .of(
        yup
          .string()
          .required()
          .test("is-hostname", function (value) {
            if (!value) return false;
            if (!hostnameRe.test(value)) {
              return this.createError({
                message: `Invalid hostname: "${value}"`,
              });
            }
            return true;
          })
      )
      .min(1, "At least one hostname is required")
      .required(),

    affiliateCode: yup
      .string()
      .required("affiliateCode is required")
      .test(
        "is-valid-affiliation",
        ({ value }) =>
          `affiliateCode "${value}" is invalid. Allowed values: ${allowedAffiliationCodes.join(", ")}`,
        (value) =>
          allowedAffiliationCodes.length === 0 ||
          (!!value && allowedAffiliationCodes.includes(value))
      ),

    enableCoupons: yup.boolean().required(),
    header: yup.object({
      backgroundColor: hexColorStr.optional(),
      height: cssLengthStr.optional(),
      showHomeLink: yup.boolean().required(),
      useDarkFont: yup.boolean().optional().default(true),
      returnUrls: yup.array()
        .of(linkItem.required())
        .min(0)
        .test("unique-locales", "returnUrls must have unique locales", (items) => {
          if (!items) return true;
          const seen = new Set();
          for (const it of items) {
            if (seen.has(it.locale)) return false;
            seen.add(it.locale);
          }
          return true;
        })
        .required(),
    }).required(),

    head: yup.array().of(headItemSchema).min(0).optional(),

    cta: yup.object({
      fontColor: hexColorStr.optional(),
      radius: cssLengthStr.optional(),
    }).required(),

    footer: yup.object({
      backgroundColor: hexColorStr.optional(),
      useDarkFont: yup.boolean().optional().default(true),
      links: yup.array().of(linkItem.required()).min(0).required(),
    }).required(),

    searchMask: yup.object({
      backgroundColor: hexColorStr.optional(),
      hideFilters: yup.boolean().optional(),
      infoSectionFontColorWhite: yup.boolean().optional(),
      popularPorts: yup.array().of(yup.string().required()).optional(),
      backgroundImage: urlStr.optional(),
    }).required(),

    showPoweredBy: yup.boolean().required(),

    colors: yup.array().of(
      yup.object({
        key: yup.string()
          .required()
          .oneOf(COLOR_DEFINITIONS.map(def => def.key), 'Invalid color key'),
        value: yup.string()
          .test(
            "color-or-length-or-empty",
            "Value must be empty, a hex color",
            (v) => v === '' || hexColorRe.test(v)
          )
          .optional(),
      })
    ).optional(),

    localization: yup.object({
      languages: yup.array().of(yup.string().required()).min(1).optional(),
      currencies: yup.array().of(yup.string().required()).min(1).optional(),
      defaultLanguage: yup.string().optional(),
      defaultCurrency: yup.string().optional(),
    }).optional(),

    social: yup.object({
      facebook: urlStr.nullable(),
      tiktok: urlStr.nullable(),
      instagram: urlStr.nullable(),
      linkedin: urlStr.nullable(),
      x: urlStr.nullable(),
    }).required(),

    communication: yup.object({
      email: communicationEmailSchema.required(),
    }).required(),
  }).required();
